import { z } from "zod";
import { AttendanceScoreAction } from "./constants";
import { WorkerStage, WorkerSubstage } from "@src/appV2/redesign/utils/worker";

const shift = z.object({
  id: z.string(),
  facilityName: z.string().nullish(),
  facilityTimezone: z.string().nullish(),
  agentReq: z.string().nullish(),
  name: z.string().nullish(),
  start: z.string().nullish(),
  end: z.string().nullish(),
  time: z.number().nullish(),
  pay: z.number().nullish(),
  createdAt: z.string().nullish(),
  agentId: z.string().nullish(),
});

const clipboardScore = z.object({
  id: z.number().positive().int(),
  shiftId: z.string().nullish(),
  workplaceId: z.string().nullish(),
  policyId: z.number().positive().int(),
  action: z.nativeEnum(AttendanceScoreAction),
  displayAction: z.string().nullish(),
  reason: z.string().nullish(),
  shiftStart: z.string().nullish(),
  targetArrivalTime: z.string().nullish(),
  clockInTime: z.string().nullish(),
  facilityName: z.string().nullish(),
  facilityTimezone: z.string().nullish(),
  acceptedDispute: z.boolean().nullish(),
  scoreReversalReason: z.string().nullish(),
  createdAt: z.string(),
  clipboardScoreUpdatedScore: z.number().nullish(),
  clipboardScoreAdjustment: z.number().nullish(),
  clipboardScoreMultiplier: z.string(),
  performedBy: z.string().nullish(),
  scoreCorrectionReason: z.string().nullish(),
  shift: shift.optional(),
});

export const clipboardScoreHistoryResponse = z.object({
  itemCount: z.number(),
  scoreLogs: z.array(clipboardScore),
});

export type ScoreLog = z.infer<typeof clipboardScore>;

export interface ClipboardScorePenaltyMultiplier {
  cancellation: {
    next: number;
    previous: number;
    subsequent: number;
  };
  lateness: {
    next: number;
    previous: number;
    subsequent: number;
  };
}

export interface CancelShiftPolicy {
  points: number;
  leadTime: number | null[];
}

export interface GenericPointsPolicy {
  points: number;
}

export interface MinutesLateRange {
  startFrom?: number | null;
  stopAt?: number | null;
}

export interface LatenessPolicy {
  points: number;
  minutesLateRange: MinutesLateRange;
}

export interface ShiftClockInConfig {
  points: number;
  requiredClockInMinutesBeforeShift: number | null;
  requiredArrivedMinutesBeforeShift: number | null;
}

export interface WorkerPerformanceMetrics {
  performanceSince: string;
  completedShifts: number;
  cancelledShifts: number;
  totalNCNS: number;
  shortNoticeCancellations: number;
}

export interface ClipboardScoreRank {
  rank: number;
  outOf: number;
}

export interface WorkerAttendanceProfile {
  restrictionCount?: number;
  clipboardScore: number;
  policy?: {
    qualityBonus?: GenericPointsPolicy;
    workShift?: GenericPointsPolicy;
    finalRestriction?: {
      window: number;
    };
    cancelShift: CancelShiftPolicy[];
    noCallNoShow?: GenericPointsPolicy;
    latenessConfig: LatenessPolicy[];
    shiftClockInConfig?: ShiftClockInConfig;
  };
  clipboardScorePenaltyMultipliers?: ClipboardScorePenaltyMultiplier;
  performanceMetrics?: WorkerPerformanceMetrics;
  clipboardScoreRank?: ClipboardScoreRank;
  accountStatus?: {
    status: WorkerStage;
  };
  accountSubstatus?: WorkerSubstage;
}

export interface AdjustScoreArgs {
  score: number;
  reason: string;
  workerUserId: string;
  allowStatusUpdate: boolean;
}

export interface ScoreAdjustmentModalArgs {
  isOpen: boolean;
  currentScore: number;
  onClose: () => void;
  workerUserId: string;
  onScoreAdjust: () => void;
}

export interface WorkersAttendanceRateRequest {
  workerUserIds: string[];
  inPastDays?: number;
}

export interface GetRestrictionResponse {
  id: number;
  duration: number;
  scoreLogId?: number;
  reason: string | null;
  createdAt: Date | string;
  processedAt: Date | string | null;
  isActive: boolean;
  endsAt: Date | string;
}

export interface WorkerAttendanceScoreDispute {
  shiftId: string;
  category?: string;
  reason: string;
  acceptedBy: string;
  acceptedAt: string;
}
